import React, { useState } from "react";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { client } from "@/lib/api";
import { HandHeart, Loader2 } from "lucide-react";

const CONTEXTS = [
  { id: "craving", label: "A strong craving" },
  { id: "stress", label: "A stressful moment" },
  { id: "habit", label: "An automatic urge" },
  { id: "after_eating", label: "After eating" },
  { id: "driving", label: "On the road" },
  { id: "social", label: "A social situation" },
  { id: "boredom", label: "A restless moment" },
  { id: "emotional", label: "A hard emotional moment" },
  { id: "alcohol_or_other", label: "Other triggers nearby" },
  { id: "something_else", label: "Something else" },
];

export function PrayerRequestDialog({ open, onOpenChange, substance = "cigarettes", onCreated }) {
  const [trigger, setTrigger] = useState(null);
  const [shareName, setShareName] = useState(true);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!trigger || sending) return;
    setSending(true);
    try {
      const { data } = await client.post("/prayer/request", {
        trigger,
        substance,
        anonymous: !shareName,
      });
      onCreated?.(data.request);
      onOpenChange(false);
      setTrigger(null);
      setShareName(true);
    } catch (e) {
      /* ignore */
    }
    setSending(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      testid="prayer-request-dialog"
      title="Ask someone to pray with you"
      description="No shame here. Choose what you're facing and, if you like, share your first name — others on the wall will stand with you."
    >
      <div className="space-y-5 pb-2">
        <div>
          <p className="text-sm font-medium text-olive mb-3">What are you facing?</p>
          <div className="grid grid-cols-2 gap-2">
            {CONTEXTS.map((c) => (
              <button
                key={c.id}
                data-testid={`prayer-context-${c.id}`}
                onClick={() => setTrigger(c.id)}
                className={`rounded-xl border px-3 py-2.5 text-sm text-left transition-colors ${
                  trigger === c.id
                    ? "border-brand-teal bg-brand-teal/10 text-brand-teal-dark font-medium"
                    : "border-border hover:bg-sage"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="prayer-name-toggle"
          onClick={() => setShareName((s) => !s)}
          className="flex items-center justify-between w-full rounded-xl border border-border px-4 py-3"
        >
          <span className="text-olive text-sm">{shareName ? "Share my first name" : "Post anonymously"}</span>
          <span className={`relative w-11 h-6 rounded-full transition-colors ${shareName ? "bg-brand-teal" : "bg-stone/40"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${shareName ? "translate-x-5" : ""}`} />
          </span>
        </button>

        <button
          data-testid="prayer-request-submit"
          onClick={submit}
          disabled={!trigger || sending}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-teal-dark px-5 py-3.5 text-white font-semibold transition-colors duration-200 hover:bg-[#0A6F66] disabled:opacity-60"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
          Send prayer request
        </button>
        <p className="text-xs text-stone text-center">
          Requests rest on the wall for 72 hours. Only a first name is ever shown.
        </p>
      </div>
    </ResponsiveModal>
  );
}
